import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { formatPrice } from "../lib/cart";
import { useT } from "../lib/i18n";

const STATE_KEY = "aabb:selected_state_v1";

export default function CheckMyPriceModal({ open, onClose }) {
  const nav = useNavigate();
  const t = useT();
  const [states, setStates] = useState([]);
  const [stateCode, setStateCode] = useState(localStorage.getItem(STATE_KEY) || "");
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/states").then(r => setStates(r.data)).catch(()=>{});
  }, [open]);

  useEffect(() => {
    if (!stateCode) { setCatalog(null); return; }
    api.get(`/catalog?state=${stateCode}`).then(r => setCatalog(r.data));
    localStorage.setItem(STATE_KEY, stateCode);
  }, [stateCode]);

  if (!open) return null;

  const goProgram = (slug) => { onClose(); nav(`/intake?program=${slug}`); };
  const goProduct = (slug) => { onClose(); nav(`/intake?product=${slug}`); };

  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, zIndex:9999, background:"rgba(0,0,0,.7)",
      display:"flex", alignItems:"flex-start", justifyContent:"center", overflowY:"auto", padding:"40px 16px"
    }} data-testid="cmp-modal">
      <div onClick={(e)=>e.stopPropagation()} style={{
        background:"var(--dark)", border:"1px solid rgba(193,127,58,.3)", borderRadius:14,
        maxWidth: 920, width:"100%", padding:"34px 30px 30px", position:"relative",
        color:"var(--white)", fontFamily:"var(--font-body)"
      }}>
        <button onClick={onClose} aria-label="Close" data-testid="cmp-close" style={{
          position:"absolute", top:14, right:14, background:"none", border:"none", color:"var(--white)",
          fontSize:"1.6rem", cursor:"pointer"
        }}>×</button>

        {!catalog ? (
          <>
            <h2 className="aa-h2" style={{fontFamily:"var(--font-display)"}}>{t("modal.where_located")}</h2>
            <p className="aa-sub">{t("modal.state_determines")}</p>
            <select className="aa-select" value={stateCode} onChange={(e)=>setStateCode(e.target.value)} data-testid="cmp-state-select">
              <option value="">{t("modal.select_state")}</option>
              {states.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          </>
        ) : (
          <>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18}}>
              <div>
                <div className="aa-eyebrow">{t("modal.pricing_in")} {states.find(s=>s.code===stateCode)?.name || stateCode}</div>
                <h2 className="aa-h2" style={{fontFamily:"var(--font-display)", marginTop:4}}>{t("modal.your_pricing")}</h2>
              </div>
              <button onClick={()=>{ setCatalog(null); }} data-testid="cmp-change-state" style={{
                background:"transparent", border:"1px solid var(--gold)", color:"var(--gold)", padding:"8px 16px",
                fontSize:".75rem", letterSpacing:".14em", textTransform:"uppercase", cursor:"pointer"
              }}>{t("modal.change_state")}</button>
            </div>

            <h3 style={{fontFamily:"var(--font-display)", color:"var(--gold)", marginBottom:10}}>{t("modal.programs")}</h3>
            <div style={{display:"grid", gap:10, gridTemplateColumns:"repeat(auto-fit, minmax(220px,1fr))", marginBottom:24}}>
              {catalog.programs.map(p => (
                <div key={p.slug} className="aa-card" style={{cursor:"default"}}>
                  {p.badge === "most_popular" && <span className="aa-badge">{t("modal.most_popular")}</span>}
                  {p.badge === "maximum" && <span className="aa-badge">{t("modal.maximum")}</span>}
                  <div className="aa-card-title">{p.name}</div>
                  <div className="aa-card-sub">{p.tagline}</div>
                  <div style={{fontFamily:"var(--font-display)", color:"var(--gold)", fontSize:"1.6rem", margin:"10px 0"}}>
                    {formatPrice(p.monthly_cents)}/mo
                  </div>
                  <button className="aa-btn" onClick={()=>goProgram(p.slug)} data-testid={`cmp-program-${p.slug}`}>{t("modal.add_to_plan")}</button>
                </div>
              ))}
            </div>

            <h3 style={{fontFamily:"var(--font-display)", color:"var(--gold)", marginBottom:10}}>{t("modal.individual_products")}</h3>
            <div style={{display:"grid", gap:10, gridTemplateColumns:"repeat(auto-fit, minmax(220px,1fr))"}}>
              {catalog.products.map(p => {
                const d = p.doses[0];
                if (!d) return null;
                const crossedPrice = Math.round(d.membership_cents * 1.10);
                return (
                  <div key={p.slug} className="aa-card" style={{cursor:"default"}}>
                    <div className="aa-card-title">{p.name}</div>
                    <div className="aa-card-sub">{p.tagline}</div>
                    <div style={{display:"flex", gap:10, alignItems:"baseline", margin:"10px 0"}}>
                      <span style={{fontFamily:"var(--font-display)", color:"var(--gold)", fontSize:"1.4rem"}}>
                        {formatPrice(d.membership_cents)}/mo
                      </span>
                      <span style={{color:"var(--muted)", textDecoration:"line-through", fontSize:".9rem"}}>
                        {formatPrice(crossedPrice)}
                      </span>
                    </div>
                    <button className="aa-btn" onClick={()=>goProduct(p.slug)} data-testid={`cmp-product-${p.slug}`}>{t("modal.add_to_plan")}</button>
                  </div>
                );
              })}
            </div>
            <p className="aa-caption" style={{marginTop:18}}>{t("modal.membership_note")}</p>
          </>
        )}
      </div>
    </div>
  );
}
